import { useTranslation } from "react-i18next"

import {
  CheckCircle2,
  CircleHelp,
  CircleSlash,
  Loader2,
  type LucideIcon,
  TriangleAlert,
  XCircle,
} from "@/components/icons"
import { Badge } from "@/components/ui/badge"

import {
  HOSTED_AGENT_EVAL_CASE_DETAIL_DEFAULTS,
  HOSTED_AGENT_EVAL_DETAIL_DEFAULTS,
  HOSTED_AGENT_EVAL_KIND_DEFAULTS,
  HOSTED_AGENT_EVAL_STATUS_DEFAULTS,
  hostedAgentEvalOutcomeVariant,
} from "../utils/hosted-agent-evals"

// The three-valued verdict, rendered so it cannot be misread (#3028).
//
// Every mark carries ICON + SHAPE + COLOUR, never colour alone: `passed` and
// `failed` are the only two outcomes that were actually measured, so they are
// the only two that get green and red. `indeterminate`, `errored` and
// `skipped` all mean "no measurement" and share the amber/grey family plus a
// dashed row border — an evidence hole has to look like a hole.

const OUTCOME_ICONS: Record<string, LucideIcon> = {
  passed: CheckCircle2,
  failed: XCircle,
  indeterminate: CircleHelp,
  errored: TriangleAlert,
  skipped: CircleSlash,
  // A suite still in flight is not a verdict of any kind — it gets the moving
  // icon so it can never be read as "we tried and could not measure it".
  running: Loader2,
}

/**
 * The verdict chip for one expectation, case or suite. `status` accepts the
 * expectation outcomes (passed/failed/indeterminate) and the two extra case
 * statuses (errored/skipped); an unknown value degrades to the amber
 * "not measured" family rather than being silently shown as a pass.
 */
export function HostedAgentEvalOutcomeBadge({
  status,
  className,
}: {
  status: string
  className?: string
}) {
  const { t } = useTranslation()
  const Icon = OUTCOME_ICONS[status] ?? CircleHelp
  const label = t(`adminDashboard.hostedAgents.evals.outcomes.${status}`, {
    defaultValue: HOSTED_AGENT_EVAL_STATUS_DEFAULTS[status] ?? status,
  })
  return (
    <Badge className={className} variant={hostedAgentEvalOutcomeVariant(status)}>
      <Icon aria-hidden className={`mr-1 size-3 ${status === "running" ? "animate-spin" : ""}`} />
      {label}
    </Badge>
  )
}

/**
 * The reason an expectation could not be measured, from our OWN i18n map. The
 * server also ships a `detail` string, but it is English prose for logs: this
 * component keys off the stable `detail_code` only, and an unrecognised code
 * falls back to the raw code in monospace so it is obviously a machine value.
 */
export function HostedAgentEvalDetailCodeText({ code }: { code: string }) {
  const { t } = useTranslation()
  if (!code) return null
  const fallback = HOSTED_AGENT_EVAL_DETAIL_DEFAULTS[code]
  if (!fallback) {
    return <span className="font-mono text-xs">{code}</span>
  }
  return (
    <span>
      {t(`adminDashboard.hostedAgents.evals.detailCodes.${code}`, { defaultValue: fallback })}
    </span>
  )
}

/** Case-level reason code — same rules, separate vocabulary and i18n map. */
export function HostedAgentEvalCaseDetailCodeText({ code }: { code: string }) {
  const { t } = useTranslation()
  if (!code) return null
  const fallback = HOSTED_AGENT_EVAL_CASE_DETAIL_DEFAULTS[code]
  if (!fallback) {
    return <span className="font-mono text-xs">{code}</span>
  }
  return (
    <span>
      {t(`adminDashboard.hostedAgents.evals.caseDetailCodes.${code}`, { defaultValue: fallback })}
    </span>
  )
}

/** Plain-language name for an expectation kind (never the raw enum value). */
export function HostedAgentEvalKindLabel({ kind }: { kind: string }) {
  const { t } = useTranslation()
  const fallback = HOSTED_AGENT_EVAL_KIND_DEFAULTS[kind]
  if (!fallback) return <span className="font-mono text-xs">{kind}</span>
  return <>{t(`adminDashboard.hostedAgents.evals.kinds.${kind}`, { defaultValue: fallback })}</>
}
